import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import axios from 'axios';
import { getAPIUrls } from './api';
import { bearerHeaders } from './authHeader';

/**
 * KST 오늘 날짜 문자열 반환 (YYYY-MM-DD)
 */
const getKstToday = () => {
    const now = new Date();
    const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    return kst.toISOString().split('T')[0];
};

const getAttendanceGuardKey = (userId, environment) =>
    `attendance_claimed_${userId}_${environment}`;

/** 오늘 출석을 이미 했는지. 저장값만 보고 서버는 호출하지 않는다. */
export const isAttendanceClaimedLocally = (userId, environment) => {
    if (!userId) return false;
    try {
        return localStorage.getItem(getAttendanceGuardKey(userId, environment)) === getKstToday();
    } catch (e) {
        return false;
    }
};

/**
 * 출석 포인트 요청
 * 💰 [비용 최적화] 클라이언트 중복 방지 가드 추가 — 당일 이미 클레임한 경우 서버 호출 스킵
 * @param {string} userId
 * @param {string} environment
 * @returns {Promise<{success: boolean, reward: number, streak: number, alreadyClaimed: boolean}>}
 */
export const claimAttendance = async (userId, environment) => {
    // 📦 클라이언트 중복 방지: 오늘 이미 클레임했으면 서버 호출 스킵
    const today = getKstToday();
    const guardKey = getAttendanceGuardKey(userId, environment);
    try {
        const storedDate = localStorage.getItem(guardKey);
        if (storedDate === today) {
            // 서버 호출 없이 즉시 반환
            return { success: false, alreadyClaimed: true, streak: 0, reward: 0 };
        }
    } catch (e) { /* localStorage 접근 실패 시 무시하고 정상 호출 진행 */ }

    try {
        const urls = getAPIUrls(environment);
        // Use the newly exported function URL logic (usually base + claimAttendance)
        // Assuming standard naming convention in api.js or direct construction if not in list
        const apiUrl = `${urls.claimAttendance || urls.apiBase + '/claimAttendance'}`;

        // Fallback if claimAttendance is not explicitly in getAPIUrls yet, construct from apiBase
        // Note: ensure getAPIUrls returns base URLs correctly. 
        // Usually endpoint is /claimAttendance

        const response = await axios.post(apiUrl, {
            userId,
            environment
        }, { headers: await bearerHeaders() });

        const result = response.data;

        // 서버 성공 시 오늘 날짜 기록 (이후 중복 호출 차단)
        if (result.success || result.alreadyClaimed) {
            try { localStorage.setItem(guardKey, today); } catch (e) { /* ignore */ }
        }

        return result;
    } catch (error) {
        console.error('Attendance Claim Error:', error);
        throw error;
    }
};

/**
 * 출석 상태 조회 (DB 직접 조회 - 비용 절감)
 * @param {string} userId 
 * @param {string} environment 
 * @returns {Promise<{lastDate: string, streak: number, todayClaimed: boolean}>}
 */
export const checkAttendanceStatus = async (userId, environment) => {
    try {
        const colPrefix = ['testnet', 'dev', 'sandbox'].includes(environment) ? 'testnet_' : '';
        const userRef = doc(db, `${colPrefix}users`, userId);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) return null;

        const data = userSnap.data();
        const attendance = data.attendance || { lastDate: '', streak: 0 };

        // KST Today Check
        const now = new Date();
        const kstDate = new Date(now.getTime() + (9 * 60 * 60 * 1000));
        const today = kstDate.toISOString().split('T')[0];

        const todayClaimed = attendance.lastDate === today;
        if (todayClaimed) {
            try { localStorage.setItem(getAttendanceGuardKey(userId, environment), today); } catch (e) { /* ignore */ }
        }
        return {
            ...attendance,
            todayClaimed
        };
    } catch (error) {
        console.error('Check Attendance Error:', error);
        return null;
    }
};
